import { Editor } from 'grapesjs';
import { faCode } from '@fortawesome/pro-solid-svg-icons/faCode';
import { getLocalizedProperty, htmlSanitize } from '@loyalty/shared-common';
import { CustomHtmlModal, CustomHtmlProps } from './CustomHtmlModal';
import { EditorComponentDefinition } from '../types';
import { EditorContext } from '../../../services/editor-service';
import {
  GrapesjsComponent,
  renderLocalizedComponent,
} from '../../../helpers/render-localized-component';
import { I18nContent, Toolbar } from '../../utils';
import { translateAttributes } from '../../../services/copy-paste/copy-paste-utils';
import { updateI18nAttributes } from '../i18n';

const CLASSNAME = 'qlfeditor-text';
const keyCustomCode = 'custom-code-plugin__code';

export const customHtmlComponentDefinition: EditorComponentDefinition = {
  id: 'custom-html',
  icon: faCode,
  category: 'Interactive elements',
  getLabel: ({ t }) => t('editor.component.html'),
  component: CustomHtmlComponent,
  copyPasteHelper: (element, context) => translateAttributes(element, context.destinationLocales),
};

export function CustomHtmlComponent(editor: Editor) {
  const languages = editor.Config?.context?.languages || [];
  const i18n = editor.I18n;

  const i18nAttributes: I18nContent = Object.fromEntries(
    languages.map((l: string) => [l, { content: i18n.t('editor.custom-code.default', { l }) }]),
  );

  editor.DomComponents.addType(customHtmlComponentDefinition.id, {
    model: {
      defaults: {
        editable: true,
        traits: [],
        i18nAttributes,
      },
      initToolbar: Toolbar,

      toHTML() {
        const { i18nAttributes } = this.attributes;
        const locale = i18n.getLocale();
        const content = htmlSanitize(getLocalizedProperty({ i18nAttributes }, 'content', locale));
        return `
          <div class="${CLASSNAME}">
            ${content}
          </div>
        `;
      },
    },
    view: {
      events: {
        dblclick: 'onActive',
      },
      init({ model }: { model: any }) {
        this.listenTo(model, 'component:update', (e: any) => {
          // when the modal is closed, the component:update callback is called twice, once with the changed content and another time with undefined
          const newCode = e?.changed[keyCustomCode];
          if (!newCode) {
            return;
          }

          updateI18nAttributes.call(this, i18n.getLocale(), { content: newCode });

          setTimeout(() => this.model.view.onRender());
        });
      },
      onRender() {
        const locale = i18n.getLocale();
        const content = getLocalizedProperty(
          { i18nAttributes: this.model.attributes.i18nAttributes },
          'content',
          locale,
        );
        this.model.attributes.i18nAttributes[locale] = { content: htmlSanitize(content) };
        renderLocalizedComponent(this as GrapesjsComponent, i18n);
      },
      onActive() {
        const locale = i18n.getLocale();
        const i18nAttributes = this.model.attributes.i18nAttributes;
        const localizedContent = getLocalizedProperty({ i18nAttributes }, 'content', locale);
        (editor.getConfig('context') as EditorContext).openModal(props => ({
          content: (
            <CustomHtmlModal
              {...props}
              locale={locale}
              data={{ content: localizedContent }}
              callback={(data: CustomHtmlProps) => {
                this.model.attributes.i18nAttributes = {
                  ...this.model.attributes.i18nAttributes,
                  [locale]: { ...i18nAttributes[locale], ...data },
                };
                this.onRender?.();
                // force the editor to detect the change and switch to saveable
                this.model.addAttributes({ forceUpdate: Date.now() });
              }}
            />
          ),
        }));
      },
    },
  });
}
